// plan-modusign-style-acceptance §Phase2 — 서명 발송 목록 페이지.
// Route: /field/sign/documents (FIELD_MANAGER + ADMIN + 제조팀 읽기 전용)
//
// 개선 9건 차수1(26-08-04) §4 (결정 ③A) — 제조팀(MFG_MANAGER) 열람 개방.
// 새 발송·양식 관리·취소·삭제는 서버 WRITE_ROLES 뿐이라 그 집합 밖에서는 버튼을
// 렌더하지 않는다(canWrite). 상세 진입과 다운로드는 그대로 열려 있다.

import { useEffect, useState, type CSSProperties } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { filterSignDocuments } from './sign-document-search';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type {
  SignatureDocumentStatus,
  SignatureDocumentType,
  SignatureDocumentSummaryDto,
} from '@bhops/shared';
import { SIGNATURE_DOCUMENT_TYPE_LABELS } from '@bhops/shared';
import { signatureDocumentsApi } from '@/api/signatureDocuments';
import { extractApiError } from '@/api/client';
// 추가수정 2차(26-07-27) §2 — 표시 날짜는 KST 정본으로.
import { kstYmd } from '@/lib/format-date';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/Form';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Drawer } from '@/components/Drawer';
import { useToast } from '@/components/Toast';
import { SignDocumentNewModal } from './SignDocumentNewModal';
import { SignTemplatesPage } from './SignTemplatesPage';
// 개선 9건 차수1(26-08-04) §4 — 쓰기 권한 정본(서버 WRITE_ROLES 거울).
import { canWriteSignDocument } from './sign-access';

// 문서유형 라벨은 shared 의 canonical 맵을 쓴다(중복·불일치 제거).
const DOC_TYPE_LABEL = SIGNATURE_DOCUMENT_TYPE_LABELS;

const STATUS_LABEL: Record<SignatureDocumentStatus, string> = {
  DRAFT: '초안',
  IN_PROGRESS: '진행 중',
  COMPLETED: '완료',
  CANCELLED: '취소',
};

// S3 Phase 3.6 (spec §3.7) — 서명 관리 전체 폭. 기존 maxWidth 1200 제거,
// AppShell 메인 폭 전체 사용 (다른 페이지와 UI 통일).
const wrap: CSSProperties = { padding: '24px 28px' };
const header: CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 };
const tableWrap: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  overflow: 'hidden',
};
const th: CSSProperties = {
  background: 'var(--bg-surface)',
  borderBottom: '1px solid var(--border-subtle)',
  padding: '10px 14px',
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-secondary)',
};
const td: CSSProperties = {
  padding: '10px 14px',
  fontSize: 13,
  borderBottom: '1px solid var(--border-subtle)',
  verticalAlign: 'middle',
};

function statusPill(status: SignatureDocumentStatus) {
  const colors: Record<SignatureDocumentStatus, { bg: string; color: string }> = {
    DRAFT: { bg: 'var(--bg-sunken)', color: 'var(--fg-muted)' },
    IN_PROGRESS: { bg: 'var(--bg-info-subtle)', color: 'var(--fg-brand)' },
    COMPLETED: { bg: 'var(--bg-success-subtle)', color: 'var(--robot-operating)' },
    CANCELLED: { bg: 'var(--bg-danger-subtle)', color: 'var(--fg-danger)' },
  };
  const c = colors[status];
  return (
    <span
      style={{
        background: c.bg,
        color: c.color,
        borderRadius: 'var(--r-pill)',
        padding: '2px 8px',
        fontSize: 11,
        fontWeight: 500,
      }}
    >
      {STATUS_LABEL[status]}
    </span>
  );
}

function signatoryProgress(doc: SignatureDocumentSummaryDto) {
  const signed = doc.signatories.filter((s) => s.status === 'SIGNED').length;
  const total = doc.signatories.length;
  return `${signed}/${total}`;
}

export function SignDocumentsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  // 현장 앱 '오늘 방문'에서 서명 받을 문서가 없을 때 넘어오는 링크
  // (?new=1&customerId=…&customerName=…) — 그 고객사를 골라 둔 채 '새 서명 생성'을 연다.
  // 한 번 열고 나면 쿼리를 지워 새로고침에 다시 뜨지 않게 한다.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkCustomerId = searchParams.get('customerId');
  const [linkedCustomer] = useState(() =>
    searchParams.get('new') === '1' && linkCustomerId
      ? { id: linkCustomerId, displayName: searchParams.get('customerName') || '선택한 고객사' }
      : null,
  );
  const [newOpen, setNewOpen] = useState(linkedCustomer !== null);
  useEffect(() => {
    if (searchParams.has('new')) setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);
  // S3 Phase 3.6 (spec §3.7) — 양식 관리 우측 드로어. 발송 목록 맥락을
  // 유지한 채 양식 CRUD 를 노출. 닫으면 발송 목록으로 복귀 (라우트 이동 없음).
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<SignatureDocumentStatus | ''>('');
  const [typeFilter, setTypeFilter] = useState<SignatureDocumentType | ''>('');
  const [cancelTarget, setCancelTarget] = useState<{ id: string } | null>(null);
  // Round 2 Phase G (#5, 결정 D-6) — soft-delete 대상.
  const [deleteTarget, setDeleteTarget] = useState<{ id: string } | null>(null);
  // data-lifecycle §3/D3·D4 — 셀프 완전삭제 대상(2단계 확인).
  const [hardDeleteTarget, setHardDeleteTarget] = useState<{ id: string } | null>(null);
  // data-lifecycle §3 — 삭제됨 문서 포함 토글(서명 운영 역할 전용).
  const [includeDeleted, setIncludeDeleted] = useState(false);
  /** 검색(2026-09-23) — 문서명·양식·고객사·로봇 시리얼·서명자. 화면에서 바로 거른다. */
  const [search, setSearch] = useState('');
  const userRole = useAuthStore((s) => s.user?.role);
  // 개선 9건 차수1(26-08-04) §4 — 인라인 role 비교를 정본 헬퍼로 교체(서버
  // WRITE_ROLES 와 한 곳에서 대응된다). 값은 종전과 동일: ADMIN / FIELD_MANAGER.
  const canWrite = canWriteSignDocument(userRole);

  const q = useQuery({
    queryKey: ['signature-documents', { statusFilter, typeFilter, includeDeleted }],
    queryFn: () =>
      signatureDocumentsApi.list({
        status: statusFilter || undefined,
        documentType: typeFilter || undefined,
        includeDeleted: includeDeleted || undefined,
      }),
  });

  const cancelMutation = useMutation({
    mutationFn: (id: string) => signatureDocumentsApi.cancel(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signature-documents'] });
      toast.success('서명 요청이 취소되었습니다.');
      setCancelTarget(null);
    },
    onError: (err) => toast.error(extractApiError(err)),
  });

  // Round 2 Phase G (#5, 결정 D-6) — soft-delete. 감사 트레일은 백엔드에서 보존.
  const deleteMutation = useMutation({
    mutationFn: (id: string) => signatureDocumentsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signature-documents'] });
      toast.success('서명 문서가 삭제되었습니다.');
      setDeleteTarget(null);
    },
    onError: (err) => toast.error(extractApiError(err)),
  });

  // data-lifecycle §3 — soft-delete 복구(셀프, 서명 운영 역할).
  const restoreMutation = useMutation({
    mutationFn: (id: string) => signatureDocumentsApi.restore(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signature-documents'] });
      toast.success('서명 문서가 복구되었습니다.');
    },
    onError: (err) => toast.error(extractApiError(err)),
  });

  // data-lifecycle §3/D3·D4 — 셀프 완전삭제(되돌릴 수 없음).
  const hardDeleteMutation = useMutation({
    mutationFn: (id: string) => signatureDocumentsApi.hardDelete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signature-documents'] });
      toast.success('서명 문서가 완전히 삭제되었습니다.');
      setHardDeleteTarget(null);
    },
    onError: (err) => toast.error(extractApiError(err)),
  });

  const allRows: SignatureDocumentSummaryDto[] = q.data ?? [];
  const rows = filterSignDocuments(allRows, search);

  return (
    <div style={wrap}>
      <div style={header}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>서명 관리 · 페리페럴</h2>
        {/* 개선 9건 차수1(26-08-04) §4 — 양식 관리·새 발송은 쓰기 동선. 제조팀
            (읽기 전용)에는 렌더하지 않는다. */}
        {canWrite && (
          <>
            <Button
              variant="secondary"
              onClick={() => setTemplatesOpen(true)}
              style={{ marginLeft: 'auto' }}
            >
              양식 관리
            </Button>
            <Button onClick={() => setNewOpen(true)}>+ 새 서명 생성</Button>
          </>
        )}
      </div>

      {/* Filters */}
      <div
        style={{
          display: 'flex',
          gap: 10,
          marginBottom: 16,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <input
          type="search"
          data-testid="sign-doc-search"
          aria-label="서명 문서 검색"
          placeholder="문서명, 고객사, 로봇 시리얼, 서명자로 검색"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: '1 1 260px',
            maxWidth: 360,
            padding: '6px 10px',
            borderRadius: 'var(--r-input)',
            border: '1px solid var(--border-default)',
            fontSize: 13,
            background: 'var(--bg-surface)',
            color: 'var(--fg-default)',
          }}
        />
        {search.trim() && (
          <span
            style={{ fontSize: 12, color: 'var(--text-secondary)' }}
            data-testid="sign-doc-search-count"
          >
            {rows.length}건 / {allRows.length}건
          </span>
        )}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as SignatureDocumentStatus | '')}
          style={{
            padding: '6px 10px',
            borderRadius: 'var(--r-input)',
            border: '1px solid var(--border-default)',
            fontSize: 13,
            // round5 #21 — 배경/글자색 미지정 시 UA 기본(흰 배경)이 다크에서 떠 보임.
            background: 'var(--bg-surface)',
            color: 'var(--fg-default)',
          }}
        >
          <option value="">전체 상태</option>
          {(Object.keys(STATUS_LABEL) as SignatureDocumentStatus[]).map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as SignatureDocumentType | '')}
          style={{
            padding: '6px 10px',
            borderRadius: 'var(--r-input)',
            border: '1px solid var(--border-default)',
            fontSize: 13,
            // round5 #21 — 배경/글자색 미지정 시 UA 기본(흰 배경)이 다크에서 떠 보임.
            background: 'var(--bg-surface)',
            color: 'var(--fg-default)',
          }}
        >
          <option value="">전체 문서 유형</option>
          {(Object.keys(DOC_TYPE_LABEL) as SignatureDocumentType[]).map((t) => (
            <option key={t} value={t}>
              {DOC_TYPE_LABEL[t]}
            </option>
          ))}
        </select>
        {/* data-lifecycle §3 — 삭제됨 문서 포함(서명 운영 역할 전용). 복구용. */}
        {canWrite && (
          <label
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 13,
              padding: '6px 10px',
              border: '1px solid var(--border-default)',
              borderRadius: 'var(--r-input)',
              background: includeDeleted ? 'var(--bg-accent-subtle)' : 'var(--bg-surface)',
              color: includeDeleted ? 'var(--fg-brand)' : 'var(--fg-default)',
              cursor: 'pointer',
              fontWeight: includeDeleted ? 600 : 400,
            }}
            title="삭제된 서명 문서도 함께 보여 줍니다. 여기서 복구할 수 있습니다."
          >
            <input
              type="checkbox"
              checked={includeDeleted}
              onChange={(e) => setIncludeDeleted(e.target.checked)}
              style={{ margin: 0 }}
            />
            삭제됨 포함
          </label>
        )}
      </div>

      {q.isLoading && <p style={{ color: 'var(--text-secondary)' }}>불러오는 중…</p>}
      {q.isError && <p style={{ color: 'var(--color-error)' }}>{extractApiError(q.error)}</p>}

      {!q.isLoading && rows.length === 0 && allRows.length > 0 && (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-secondary)' }}>
          <p>「{search.trim()}」에 맞는 서명 문서가 없습니다.</p>
          <Button variant="secondary" onClick={() => setSearch('')}>
            검색 지우기
          </Button>
        </div>
      )}
      {!q.isLoading && allRows.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-secondary)' }}>
          <p>생성된 서명 요청이 없습니다.</p>
          {canWrite && <Button onClick={() => setNewOpen(true)}>첫 서명 생성하기</Button>}
        </div>
      )}

      {rows.length > 0 && (
        <div style={tableWrap}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>문서명</th>
                <th style={th}>문서 유형</th>
                <th style={th}>로봇</th>
                <th style={th}>고객사</th>
                <th style={th}>상태</th>
                <th style={th}>서명 진행</th>
                <th style={th}>생성일</th>
                <th style={th}>작업</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                // Round 2 Phase G (#5) — 행 전체 클릭으로 상세 진입하던 동작 제거.
                // 명시적 "상세" 버튼으로만 이동(양식표와 동일 패턴).
                // data-lifecycle §3 — 삭제됨 row 는 흐리게.
                <tr key={r.id} style={{ opacity: r.deletedAt ? 0.55 : 1 }}>
                  <td style={td}>
                    {/* sign-console-prefill — 양식명이 아니라 문서 표시명(고객사_유형_YYMMDD). */}
                    <span style={{ fontWeight: 600 }}>{r.displayName}</span>
                  </td>
                  <td style={td}>{DOC_TYPE_LABEL[r.documentType]}</td>
                  <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                    {r.robotSerial ?? '–'}
                    {/* round3 #2① — 다중 로봇 문서면 '외 N대' 표기. */}
                    {r.robots && r.robots.length > 1 && (
                      <span style={{ color: 'var(--text-secondary)', marginLeft: 4 }}>
                        외 {r.robots.length - 1}대
                      </span>
                    )}
                  </td>
                  <td style={td}>{r.customerName}</td>
                  <td style={td}>
                    {r.deletedAt ? (
                      <span style={{ color: 'var(--severity-urgent)', fontWeight: 600 }}>
                        삭제됨
                      </span>
                    ) : (
                      statusPill(r.status)
                    )}
                  </td>
                  <td style={td}>{signatoryProgress(r)}명 서명</td>
                  {/* 추가수정 2차(26-07-27) §2 — ISO 를 그대로 자르면 UTC 날짜라
                      한국 새벽 0~9시에 발송한 문서가 전날로 보였다. KST 로 환산.
                      목록이므로 날짜만(결정 ②A). */}
                  <td style={td}>{kstYmd(r.createdAt) ?? '–'}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                    {/* data-lifecycle §3 — 삭제된 문서는 상세(get)가 404 이므로 숨김. */}
                    {!r.deletedAt && (
                      <button
                        onClick={() => navigate(`/field/sign/documents/${r.id}`)}
                        style={{
                          marginRight: 8,
                          fontSize: 12,
                          padding: '3px 8px',
                          borderRadius: 'var(--r-btn)',
                          border: '1px solid var(--border-default)',
                          cursor: 'pointer',
                          background: 'var(--bg-surface)',
                        }}
                      >
                        상세
                      </button>
                    )}
                    {/* data-lifecycle §3 — 삭제됨 문서는 취소/삭제 대신 복구 + 완전삭제. */}
                    {r.deletedAt
                      ? canWrite && (
                          <>
                            <button
                              onClick={() => restoreMutation.mutate(r.id)}
                              disabled={restoreMutation.isPending}
                              style={{
                                marginLeft: 8,
                                fontSize: 12,
                                padding: '3px 8px',
                                borderRadius: 'var(--r-btn)',
                                border: '1px solid var(--fg-brand)',
                                cursor: 'pointer',
                                background: 'var(--bg-info-subtle)',
                                color: 'var(--fg-brand)',
                                fontWeight: 600,
                              }}
                            >
                              복구
                            </button>
                            {/* data-lifecycle §3/D3·D4 — 셀프 완전삭제(되돌릴 수 없음). */}
                            <button
                              onClick={() => setHardDeleteTarget({ id: r.id })}
                              title="이 서명 문서를 영구 삭제합니다. 되돌릴 수 없습니다."
                              style={{
                                marginLeft: 8,
                                fontSize: 12,
                                padding: '3px 8px',
                                borderRadius: 'var(--r-btn)',
                                border: '1px solid var(--severity-urgent)',
                                cursor: 'pointer',
                                background: 'var(--bg-surface)',
                                color: 'var(--severity-urgent)',
                                fontWeight: 600,
                              }}
                            >
                              완전 삭제
                            </button>
                          </>
                        )
                      : // 개선 9건 차수1(26-08-04) §4 — 취소·삭제는 쓰기 동선.
                        canWrite && (
                          <>
                            {(r.status === 'IN_PROGRESS' || r.status === 'DRAFT') && (
                              <button
                                onClick={() => setCancelTarget({ id: r.id })}
                                style={{
                                  fontSize: 12,
                                  padding: '3px 8px',
                                  borderRadius: 'var(--r-btn)',
                                  border: '1px solid var(--border-default)',
                                  cursor: 'pointer',
                                  background: 'var(--bg-surface)',
                                  color: 'var(--color-error)',
                                }}
                              >
                                취소
                              </button>
                            )}
                            <button
                              onClick={() => setDeleteTarget({ id: r.id })}
                              style={{
                                marginLeft: 8,
                                fontSize: 12,
                                padding: '3px 8px',
                                borderRadius: 'var(--r-btn)',
                                border: '1px solid var(--border-default)',
                                cursor: 'pointer',
                                background: 'var(--bg-surface)',
                                color: 'var(--color-error)',
                              }}
                            >
                              삭제
                            </button>
                          </>
                        )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {newOpen && canWrite && (
        <SignDocumentNewModal
          initialCustomer={linkedCustomer}
          onClose={() => setNewOpen(false)}
          onCreated={(id) => {
            setNewOpen(false);
            qc.invalidateQueries({ queryKey: ['signature-documents'] });
            navigate(`/field/sign/documents/${id}`);
          }}
        />
      )}

      {/* S3 Phase 3.6 (spec §3.7) — 양식 관리 우측 드로어. SignTemplatesPage
          를 본문 그대로 재사용 (목록 + CRUD 모달 포함). 닫으면 발송 목록 복귀.
          FB §7 — 드로어 폭 720→900 확대. 디자이너는 풀페이지 라우트라
          드로어 안에서 직접 이동하면 stale 오버레이가 남으므로, 디자이너 진입을
          여기서 가로채 드로어를 먼저 닫고(setTemplatesOpen(false)) 풀페이지로 이동. */}
      <Drawer
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        title="서명 양식 관리"
        width={900}
      >
        <SignTemplatesPage
          onOpenDesigner={(templateId) => {
            setTemplatesOpen(false);
            navigate(`/field/sign/templates/${templateId}/design`);
          }}
        />
      </Drawer>

      <ConfirmDialog
        open={!!cancelTarget}
        title="서명 요청 취소"
        message="서명 요청을 취소하시겠습니까?"
        danger
        confirmLabel="취소 처리"
        busy={cancelMutation.isPending}
        onConfirm={() => {
          if (cancelTarget) cancelMutation.mutate(cancelTarget.id);
        }}
        onCancel={() => setCancelTarget(null)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="서명 문서 삭제"
        message="이 서명 문서를 삭제하시겠습니까? 목록에서 숨겨지며 감사 로그는 보존됩니다."
        danger
        confirmLabel="삭제"
        busy={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget.id);
        }}
        onCancel={() => setDeleteTarget(null)}
      />

      {/* data-lifecycle §3/D3·D4 — 완전삭제 2단계 확인(되돌릴 수 없음). */}
      <ConfirmDialog
        open={!!hardDeleteTarget}
        title="서명 문서 완전 삭제"
        message="이 서명 문서를 영구적으로 삭제합니다. 되돌릴 수 없습니다. 이 문서로 만든 인수증이 있으면 삭제되지 않습니다. 감사 로그는 그대로 남습니다."
        danger
        confirmLabel="완전 삭제"
        busy={hardDeleteMutation.isPending}
        onConfirm={() => {
          if (hardDeleteTarget) hardDeleteMutation.mutate(hardDeleteTarget.id);
        }}
        onCancel={() => setHardDeleteTarget(null)}
      />
    </div>
  );
}
